<template>
  <div class="success-container">
    <div v-if="isMuNeedInput" class="mu-input-section">
      <div class="success-icon-wrapper">
        <div class="success-icon-circle" style="background: #e6f7ff; color: #1890ff;">
          <a-icon type="idcard" class="check-icon" />
        </div>
      </div>
      <h1 class="main-title" style="margin-top: 20px;">Student ID Required</h1>
      <p class="description">
        Please enter your 7-digit student ID to complete the check-in.
      </p>
      <div class="input-group" style="margin-top: 30px; text-align: left;">
        <label style="font-weight: bold; margin-bottom: 8px; display: block;">Student ID (7 digits)</label>
        <a-input v-model="manualStudentId" placeholder="e.g. 9981000" size="large" :maxLength="7" @keypress="onlyNumber"
          style="text-align: center; font-size: 1.2rem;" />
      </div>
      <button class="submit-btn" :disabled="manualStudentId.length !== 7" @click="handleSubmitMu"
        style="width: 100%; margin-top: 30px; padding: 12px; background: #3e247a; color: white; border-radius: 8px; font-weight: bold; border: none; cursor: pointer;">
        Submit
      </button>
    </div>

    <div v-else class="success-content">
      <div class="success-icon-wrapper">
        <div class="success-icon-circle">
          <a-icon type="check" class="check-icon" />
        </div>
      </div>

      <div class="pre-title">CHECK-IN COMPLETE</div>

      <h1 class="main-title">
        You're in, {{ displayName }}!
      </h1>

      <p class="description">
        Your attendance is recorded for <strong>Orientation {{ accountType }}</strong>.
      </p>

      <!-- Prize Box -->
      <div class="prize-box">
        <div class="prize-content">
          <a-icon type="gift" class="prize-icon" />
          <div>
            <div class="prize-title">PRIZE DRAW</div>
            <div class="prize-desc">1 entry confirmed</div>
          </div>
        </div>
        <div class="prize-badge">ELIGIBLE</div>
      </div>

      <!-- Info Table -->
      <div class="info-table">
        <div class="info-row">
          <span class="info-label">Student ID</span>
          <span class="info-value">{{ studentId }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">Account</span>
          <span class="info-value">{{ accountEmail }}</span>
        </div>
        <div class="info-row no-border">
          <span class="info-label">Checked in</span>
          <span class="info-value">{{ checkinTime }}</span>
        </div>
      </div>

      <!-- All-in-One Access Section -->
      <div v-if="accountType === 'MUIC Account'" class="all-in-one-section">
        <div class="aio-header">
          <small class="aio-title font-web">Your MUIC Account: All-in-One Access</small>
          <span class="muic-only-badge">(MUIC Account Only)</span>
        </div>
        <div class="aio-grid mt-10">
          <!-- SKY+ -->
          <a href="https://sky-training.muic.io/" target="_blank" class="aio-item link-item">
            <div class="aio-icon sky-icon">
              <span class="sky-text-top">MUIC</span>
              <span class="sky-text-main">SKY<span class="sky-plus">+</span></span>
            </div>
            <div class="aio-label">SKY+ Registration System</div>
          </a>

          <!-- e-learning -->
          <a href="https://elearning.muic.io/" target="_blank" class="aio-item link-item">
            <div class="aio-icon modern-icon-wrapper blue-bg">
              <a-icon type="desktop" style="color: #4834d4;" />
            </div>
            <div class="aio-label">MUIC e-learning</div>
          </a>

          <!-- Student Portal -->
          <a href="https://newmy.muic.io/" target="_blank" class="aio-item link-item">
            <div class="aio-icon modern-icon-wrapper purple-bg">
              <a-icon type="desktop" style="color: #722ed1;" />
            </div>
            <div class="aio-label">Student Portal: my.muic.io</div>
          </a>

          <!-- IC App: Copter -->
          <div class="aio-item">
            <div class="aio-icon" style="background: transparent; box-shadow: none; padding: 0;">
              <img src="/imageApp/MUIC_APP_512_02.png" alt="IC App"
                style="width: 100%; height: 100%; border-radius: 12px; object-fit: contain;" />
            </div>
            <div class="aio-label">IC App: Copter<br>(AI Assistant)</div>
          </div>

          <!-- WiFi -->
          <div class="aio-item">
            <div class="aio-icon modern-icon-wrapper green-bg">
              <a-icon type="wifi" style="color: #13c2c2;" />
            </div>
            <div class="aio-label">@MUIC WiFi</div>
          </div>
        </div>
      </div>
      <!-- Actions -->
      <button class="logout-btn" @click="handleLogout" :disabled="loading">
        <a-icon v-if="!loading" type="logout" style="margin-right: 8px;" />
        <a-spin v-else size="small" style="margin-right: 8px;" />
        Log out / switch account
      </button>
      <div class="note-text">
        Keep this page open if staff asks to see your confirmation. <br> Logging out does not remove your recorded
        attendance or prize entry.
      </div>
    </div>
  </div>
</template>

<script>
import { PublicClientApplication } from "@azure/msal-browser";
import axios from "axios";

export default {
  name: 'CheckInSuccess',
  props: {
    accountData: {
      type: Object,
      default: () => ({})
    },
    accountType: {
      type: String,
      default: 'MUIC Account'
    },
    roomId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      checkinTime: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      loading: false,
      manualStudentId: '',
      muSubmitted: false
    };
  },
  computed: {
    isMuNeedInput() {
      return this.accountType === 'MU Account' && !this.muSubmitted;
    },
    displayName() {
      // If the MSAL account has a name, use it. Otherwise, use a default.
      if (this.accountData && this.accountData.name) {
        return this.accountData.name.split(' ')[0]; // Use first name if possible
      }
      return 'Student';
    },
    accountEmail() {
      if (this.accountData && this.accountData.username) {
        return this.accountData.username;
      }
      return 'unknown@muic.io';
    },
    studentId() {
      if (this.accountType === 'MU Account' && this.muSubmitted) {
        return this.manualStudentId;
      }
      // Try to extract a 7 or 8 digit student ID from the email (e.g. u6480001@muic.io)
      const match = this.accountEmail.match(/\d{7,8}/);
      if (match) {
        return match[0];
      }
      return 'Unknown';
    }
  },
  methods: {
    onlyNumber(event) {
      const charCode = (event.which) ? event.which : event.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        event.preventDefault();
      }
    },
    async handleSubmitMu() {
      if (this.manualStudentId.length === 7) {
        if (this.roomId && this.accountData.accessToken) {
          try {
            await axios.post(`/orientation-game/api/v1/public/rooms/${this.roomId}/check-in/`, {
              student_id: this.manualStudentId
            }, {
              headers: { Authorization: `Bearer ${this.accountData.accessToken}` }
            });
            console.log("POST Check-in Success (MU)");
          } catch (postError) {
            console.error("POST Check-in Error:", postError);
            if (postError.response) {
              const errData = postError.response.data || {};
              const detail = errData.detail || "Student ID is not found on the active event roster.";
              this.$message.error(detail, 5);
            } else {
              this.$message.error("Check-in failed. Please try again.", 5);
            }
            await this.handleLogout();
            return;
          }
        }
        this.muSubmitted = true;
      }
    },
    async handleLogout() {
      this.loading = true;
      try {
        const msalConfig = {
          auth: {
            clientId: "16f6e5bb-4881-421a-84f5-904e2de8e368",
            authority: "https://login.microsoftonline.com/5a8c21e3-7c52-40e3-8d81-deba37efd8dc",
            redirectUri: "http://localhost:8080/orientation"
          }
        };
        const msalInstance = new PublicClientApplication(msalConfig);
        await msalInstance.initialize();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          await msalInstance.logoutPopup({
            account: accounts[0],
            postLogoutRedirectUri: window.location.origin + "/orientation"
          });
        }
      } catch (e) {
        console.error("Logout Error:", e);
      } finally {
        this.loading = false;
        this.$emit('logout');
      }
    }
  }
};
</script>

<style scoped>
.success-container {
  background: white;
  width: 100%;
  max-width: 720px;
  padding: 50px 40px;
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
  text-align: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

/* Icon */
.success-icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.success-icon-circle {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #118641;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 8px solid #e6f6eb;
}

.check-icon {
  color: white;
  font-size: 32px;
  font-weight: bold;
}

/* Typography */
.pre-title {
  color: #3e247a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 12px;
}

.main-title {
  color: #3e247a;
  font-size: 42px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 16px;
  letter-spacing: -1.5px;
}

.description {
  color: #6c757d;
  font-size: 14px;
  margin-bottom: 30px;
}

.description strong {
  color: #555;
  font-weight: 700;
}

/* Prize Box */
.prize-box {
  background: #fffdf5;
  border: 1px solid #f6e9c4;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  text-align: left;
}

.prize-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.prize-icon {
  font-size: 24px;
  color: #a4731c;
}

.prize-title {
  font-size: 10px;
  font-weight: 800;
  color: #8c5f0f;
  letter-spacing: 1px;
}

.prize-desc {
  font-size: 14px;
  font-weight: 700;
  color: #5d3d04;
}

.prize-badge {
  background: white;
  border: 1px solid #d5a543;
  color: #8c5f0f;
  font-size: 10px;
  font-weight: 800;
  padding: 4px 8px;
  border-radius: 6px;
  letter-spacing: 1px;
}

/* Info Table */
.info-table {
  border: 1px solid #eaeaea;
  border-radius: 12px;
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eaeaea;
  font-size: 13px;
}

.info-row.no-border {
  border-bottom: none;
}

.info-label {
  color: #6c757d;
  font-weight: 500;
}

.info-value {
  color: #212529;
  font-weight: 700;
}

/* Note Text */
.note-text {
  color: #6c757d;
  font-size: 12px;
  margin-bottom: 24px;
}

/* Actions */
.logout-btn {
  width: 100%;
  background: #fff1f0;
  color: #f5222d;
  border: 1px solid #ffa39e;
  padding: 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 16px;
}

.logout-btn:hover {
  background: #ffccc7;
  border-color: #ff7875;
}

.logout-note {
  color: #888;
  font-size: 11px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .success-container {
    padding: 30px 24px;
  }

  .main-title {
    font-size: 34px;
  }
}

@media (max-height: 750px) {
  .success-container {
    padding: 20px 24px;
  }

  .success-icon-wrapper {
    margin-bottom: 16px;
  }

  .prize-box,
  .info-table,
  .all-in-one-section {
    margin-bottom: 16px;
  }
}

/* All-in-One Section Styles */
.all-in-one-section {
  margin-top: 10px;
  margin-bottom: 24px;
  text-align: left;
}

.aio-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.aio-title {
  color: #6b6b6b;
  font-size: 11px;
  font-weight: 600;
}

.muic-only-badge {
  background: #f0f5ff;
  color: #2f54eb;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.aio-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 15px;
}

.aio-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.link-item {
  text-decoration: none;
  transition: transform 0.2s ease, opacity 0.2s ease;
  cursor: pointer;
}

.link-item:hover {
  transform: translateY(-3px);
  opacity: 0.9;
}

.aio-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
}

.modern-icon-wrapper {
  font-size: 26px;
}

.modern-icon-wrapper.blue-bg {
  background: #f0f5ff;
}

.modern-icon-wrapper.purple-bg {
  background: #f9f0ff;
}

.modern-icon-wrapper.green-bg {
  background: #e6fffb;
}

.sky-icon {
  background: #472d8a;
  color: white;
  flex-direction: column;
  line-height: 1;
}

.sky-text-top {
  font-size: 6px;
  font-weight: bold;
}

.sky-text-main {
  font-size: 11px;
  font-weight: 900;
}

.sky-plus {
  color: #ffd43b;
}


.aio-label {
  font-size: 12px;
  color: #555;
  line-height: 1.3;
  font-weight: 500;
}

@media (max-width: 650px) {
  .aio-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px 10px;
  }
}

@media (max-width: 450px) {
  .aio-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
